import Service from '@ember/service';
import { isDevelopingApp, macroCondition } from '@embroider/macros';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { warn } from '@ember/debug';
import { HashicorpCloudResourcemanagerResourceIDResourceType } from '@clients/cloud-resource-manager';
import { DEFAULT_ACCOUNT_ID } from 'billing-common/constants/billing';
import { PROJECT_CREATED } from 'core/utils/consts/analytics-events/platform';

export const MAX_UPDATE_ADD_PROJECT_TO_BILLING_RETRIES = 3;
export const NAME_ALREADY_EXISTS_ERROR =
  'project with name already exists in the org';

export default class ResourcesProjectService extends Service {
  @service analytics;
  @service api;
  @service abilities;
  @service billing;
  @service userContext;
  @service intl;

  // This needs to stay a lowercase string to match with the ember ability type.
  type = 'project';

  get orgId() {
    return this.userContext.organization.id;
  }

  get projects() {
    return this.userContext.projects;
  }

  edit = {
    route: 'cloud.orgs.detail.projects.detail.edit',
  };

  delete = {
    task: this.deleteProject,
  };

  /**
   *
   * @param name name of the project
   * @param description description of the project
   * @param organization organization the project will be created in
   * @param country country the user is in - for billing
   * @throws Error if project create api returns an error
   * @returns {Generator<Promise<any>, *, *>}
   */
  @task
  *_createProject(name, description, organization) {
    let resp;
    const payload = {
      name: name.trim(),
      description,
      parent: {
        id: organization.id,
        type: HashicorpCloudResourcemanagerResourceIDResourceType.ORGANIZATION,
      },
    };

    try {
      resp =
        yield this.api.resourceManager.project.projectServiceCreate(payload);
      return { payload, resp };
    } catch (e) {
      resp = yield e.json();

      switch (resp.message) {
        case NAME_ALREADY_EXISTS_ERROR: {
          resp.title = `${this.intl.t(
            'services.resources.projects.create.project-name-must-be-unique'
          )}.`;
          resp.message = this.intl.t(
            'services.resources.projects.create.the-project-name-is-already-in-use'
          );
          resp.details = [
            {
              field_violations: [
                {
                  field: 'name',
                  description: this.intl.t(
                    'services.resources.projects.create.project-name-must-be-unique'
                  ),
                },
              ],
            },
          ];
          break;
        }
      }

      const modifiedResponse = new Response(JSON.stringify(resp), {
        status: e.status,
        statusText: e.statusText,
        headers: e.headers,
      });

      throw modifiedResponse;
    }
  }

  /**
   *
   * @param name name of the project
   * @param description description of the project
   * @param organization organization the project will be created in
   * @param country country the user is in - for billing
   * @param isDefault if the project is a new organization's default project
   * @throws Error if the project was created but could not be added to the billing account
   * @returns {Generator<Promise<HashicorpCloudResourcemanagerProjectCreateResponse>|any>, *, *>}
   */
  @task
  *createProject(
    name,
    description,
    organization,
    countryCode,
    isDefault = false
  ) {
    const { payload, resp } = yield this._createProject.perform(
      name,
      description,
      organization
    );
    const { project } = resp;

    if (isDefault) {
      try {
        const billingAccount = yield this.billing.createDefaultBillingAccount(
          organization.id,
          project.id,
          countryCode
        );
        if (!billingAccount.billingAccount) {
          throw Error();
        }
      } catch {
        yield this.rollbackProjectCreation.perform({ project });
      }
    } else {
      const { updated, response } = yield* this.addProjectToBillingAccount(
        organization,
        project,
        DEFAULT_ACCOUNT_ID,
        MAX_UPDATE_ADD_PROJECT_TO_BILLING_RETRIES
      );

      // If we added the project to the billing account then update our
      // userContext with the response. Otherwise, delete the project and alert
      // the user that something went wrong. A project without a billing account
      // is not useful and this error needs to be resolved.
      if (updated) {
        this.userContext.billing = response;

        this.analytics.trackEvent(PROJECT_CREATED, {
          payload,
          resp,
        });

        // Push in the new project so that userContext knows about the projects
        // count. This is helpful for knowing when a project can or cannot be
        // removed.
        this.userContext.projects = [...this.userContext.projects, project];
        return project;
      } else {
        yield this.rollbackProjectCreation.perform({ project });
      }
    }
    return { project };
  }

  /**
   * This method attempts to set the correct billing account for the newly created project.
   * It will do different things depending on how many projects the organization currently has.
   *
   * If this is the first project in the org, then we'll try and create a default billing account for the user
   * If the org already has a project - ideally it also has a billing account,
   * and we'll try to add the new project to that billing account - with 5 retries in case this call fails.
   *
   * For the majority of the billing service's calls that we're making here, the billing service will not
   * return an error, instead returns an empty billing object - to ensure the app doesn't blow up without one.
   * However, in our case here, we'd like to know if the billing account was successfully created/updated or not, and
   * if we did not succeed, we'll return updatedBilling as false, so the calling function can handle the error
   * and the state of the user's account.
   * @param projects - the list of projects the user has before the newly created project
   * @param organization - the organization the user is creating the project in
   * @param project - the newly created project
   * @param country - the country the user is in - used to create the default billing account if necessary
   * @param maxRetries - the number of times to retry adding the project to the billing account
   * @returns {Promise<{billing, updatedBilling: boolean}>}
   */
  *addProjectToBillingAccount(
    organization,
    project,
    billingAccountId = DEFAULT_ACCOUNT_ID,
    maxRetries = MAX_UPDATE_ADD_PROJECT_TO_BILLING_RETRIES
  ) {
    for (let i = 0; i < maxRetries; i++) {
      try {
        // We attach every project to the same billing account. Previously we
        // could use any previously-created project from the projects list to
        // find the billing account id and then attach the new project to it.
        // This daisy-chaining only worked if that project, selected because it
        // was the first project in the list response, had a billing account.
        // Instead, we should just attach it to the default id.
        const response = yield this.billing.addProjectToBillingAccount(
          organization.id,
          project.id,
          billingAccountId
        );

        // If there's no billing account, throw an error so that we catch it and
        // try again. This response will always 200 but if there's no billing
        // account then it returns an empty Billing class.
        if (!response.billingAccount) {
          throw new Error();
        }

        return {
          updated: true,
          response,
        };
      } catch (e) {
        if (macroCondition(isDevelopingApp())) {
          warn('project create error', { id: 'project.create.index' });
          console.error('failed to create project', e);
        }
        continue;
      }
    }

    return {
      updated: false,
    };
  }

  @task
  *deleteProject(projectId) {
    yield this.api.resourceManager.project.projectServiceDelete(projectId);

    // Filter out the project so that userContext knows about the projects
    // count. This is helpful for knowing when a project can or cannot be
    // removed.
    this.userContext.projects = this.userContext.projects.filter((project) => {
      return project.id !== projectId;
    });
  }

  @task
  *rollbackProjectCreation({ project }) {
    // Delete the project to clean up and then throw an error to trigger
    // the FormError component.
    //yield this.api.resourceManager.project.projectServiceDelete(project.id);
    yield this.deleteProject.perform(project.id);

    const cantCreateError = new Error();

    cantCreateError.message = this.intl.t(
      'services.resources.projects.create.project-has-been-deleted-no-billing'
    );

    throw cantCreateError;
  }
}
